import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import client from '../../api/client';

const WAREHOUSES = [
  { key: 'delhi',     label: 'New Delhi',  color: 'blue' },
  { key: 'bangalore', label: 'Bangalore',  color: 'violet' },
];

const DEVICE_TYPES = [
  'PTZ Camera', 'Display / Screen', 'Dell Micro PC', 'Jabra PanaCast 50',
  'AV Controller', 'Crestron Switcher', 'Switch Matrix', 'Network Switch',
  'Microphone', 'Mercury Speaker', 'HDMI Splitter', 'Air Media',
  'USB Hub / Extender', 'Remote Control', 'Other',
];

const WH_COLOR = {
  blue:   { badge: 'bg-blue-100 text-blue-700',    dot: 'bg-blue-500',   ring: 'ring-blue-200',   btn: 'bg-blue-600 hover:bg-blue-500' },
  violet: { badge: 'bg-violet-100 text-violet-700', dot: 'bg-violet-500', ring: 'ring-violet-200', btn: 'bg-violet-600 hover:bg-violet-500' },
};

const STATUS_CONFIG = {
  available: { label: 'Available', badge: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  reserved:  { label: 'Reserved',  badge: 'bg-amber-100 text-amber-700',    dot: 'bg-amber-500' },
  faulty:    { label: 'Faulty',    badge: 'bg-red-100 text-red-600',         dot: 'bg-red-500' },
};

// ── Inline editable cell ──────────────────────────────────────────────────────
function EditableCell({ value, unitId, field, placeholder = '—', disabled = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const inputRef = useRef();

  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const save = async () => {
    setEditing(false);
    if (draft === (value || '')) return;
    try { await client.patch(`/inventory/spare-units/${unitId}/`, { [field]: draft }); }
    catch { setDraft(value || ''); }
  };

  if (editing) return (
    <input
      ref={inputRef}
      className="w-full border border-brand-300 rounded px-2 py-1 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-brand-300"
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={save}
      onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); } }}
    />
  );

  if (disabled) return (
    <span className="flex items-center gap-1">
      <span className={`font-mono text-xs ${draft ? 'text-brand-600' : 'text-gray-300 italic'}`}>
        {draft || placeholder}
      </span>
    </span>
  );

  return (
    <span
      onClick={() => setEditing(true)}
      className="cursor-pointer group/cell flex items-center gap-1"
      title="Click to edit"
    >
      <span className={`font-mono text-xs ${draft ? 'text-brand-600' : 'text-gray-300 italic'}`}>
        {draft || placeholder}
      </span>
      <svg className="w-3 h-3 text-gray-300 opacity-0 group-hover/cell:opacity-100 transition-opacity flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
      </svg>
    </span>
  );
}

// ── Add Unit Modal ────────────────────────────────────────────────────────────
function AddUnitModal({ warehouse, onClose, onAdded }) {
  const [form, setForm] = useState({ device_name: '', part_code: '', model: '', serial_number: '', notes: '', is_serialised: true, quantity: 1 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const wh = WAREHOUSES.find(w => w.key === warehouse);
  const colors = WH_COLOR[wh.color];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.device_name) { setError('Device type is required.'); return; }
    if (form.is_serialised && !form.serial_number) { setError('Serial number is required for serialised parts.'); return; }
    setSaving(true); setError('');
    try {
      await client.post('/inventory/spare-units/', { ...form, warehouse });
      onAdded(); onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add unit.');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-bold text-brand-500 text-base">Add Spare — {wh.label}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Device *</label>
            <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300 bg-white"
              value={form.device_name} onChange={e => setForm(f => ({ ...f, device_name: e.target.value }))}>
              <option value="">Select device…</option>
              {DEVICE_TYPES.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Part Code</label>
            <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-300"
              value={form.part_code} onChange={e => setForm(f => ({ ...f, part_code: e.target.value }))} placeholder="e.g. HD-DA4-4KZ-E" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Model</label>
            <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
              value={form.model} onChange={e => setForm(f => ({ ...f, model: e.target.value }))} placeholder="e.g. Crestron" />
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" checked={form.is_serialised}
                onChange={e => setForm(f => ({ ...f, is_serialised: e.target.checked, serial_number: '', quantity: e.target.checked ? 1 : f.quantity }))}
                className="w-4 h-4 accent-brand-500" />
              <span className="text-xs font-semibold text-gray-600">Serialised part</span>
            </label>
          </div>
          {form.is_serialised ? (
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Serial Number *</label>
              <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-300"
                value={form.serial_number} onChange={e => setForm(f => ({ ...f, serial_number: e.target.value }))} placeholder="Serial number" />
            </div>
          ) : (
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Quantity</label>
              <input type="number" min="1" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-300"
                value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: parseInt(e.target.value) || 1 }))} />
            </div>
          )}
          {error && <p className="text-red-500 text-xs">{error}</p>}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-200 rounded-xl py-2 text-sm font-semibold text-gray-500 hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className={`flex-1 rounded-xl py-2 text-sm font-semibold text-white ${colors.btn} disabled:opacity-50`}>
              {saving ? 'Adding…' : 'Add Spare'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Spares() {
  const user = useSelector(s => s.auth.user) || JSON.parse(localStorage.getItem('user') || '{}');
  const isReadOnly = user.role === 'RE';
  const [exporting, setExporting] = useState(false);
  const handleExportSpares = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ warehouse: activeWh });
      if (statusFilter) params.append('status', statusFilter);
      if (deviceFilter) params.append('device_name', deviceFilter);
      const resp = await client.get(`/inventory/spare-units/export/?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'spares_export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Failed to export spares.');
    } finally {
      setExporting(false);
    }
  };
  const [activeWh, setActiveWh] = useState('delhi');
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [search, setSearch] = useState('');
  const [deviceFilter, setDeviceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const wh = WAREHOUSES.find(w => w.key === activeWh);
  const colors = WH_COLOR[wh.color];

  const load = async () => {
    setLoading(true);
    try {
      const params = { warehouse: activeWh };
      if (statusFilter) params.status = statusFilter;
      if (deviceFilter) params.device_name = deviceFilter;
      const { data } = await client.get('/inventory/spare-units/', { params });
      setUnits(data.results || data);
    } catch { setUnits([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); setSearch(''); }, [activeWh, statusFilter, deviceFilter]);

  const handleDelete = async (id) => {
    try { await client.delete(`/inventory/spare-units/${id}/`); } catch {}
    setDeleteId(null); load();
  };

  const filtered = units.filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (u.device_name || '').toLowerCase().includes(q) ||
      (u.model || '').toLowerCase().includes(q) ||
      (u.part_code || '').toLowerCase().includes(q) ||
      (u.serial_number || '').toLowerCase().includes(q);
  });

  const available = units.filter(u => u.status === 'available').length;
  const reserved  = units.filter(u => u.status === 'reserved').length;
  const faulty    = units.filter(u => u.status === 'faulty').length;

  return (
    <div className="p-6 space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-brand-500">Spare Warehouses</h1>
        <p className="text-gray-400 text-sm mt-0.5">Unit-level spare inventory across New Delhi and Bangalore</p>
      </div>

      {/* Warehouse tabs */}
      <div className="flex gap-3">
        {WAREHOUSES.map(w => {
          const c = WH_COLOR[w.color];
          const isActive = activeWh === w.key;
          return (
            <button key={w.key} onClick={() => setActiveWh(w.key)}
              className={`flex items-center gap-2.5 px-5 py-3 rounded-xl border font-semibold text-sm transition-all ${
                isActive ? `border-transparent ${c.badge} shadow-sm ring-1 ${c.ring}` : 'border-gray-200 text-gray-500 hover:bg-gray-50'
              }`}
            >
              <span className={`w-2.5 h-2.5 rounded-full ${c.dot}`} />
              {w.label}
              {isActive && <span className="ml-1 text-xs opacity-70">{available} avail.</span>}
            </button>
          );
        })}
      </div>

      {/* Summary pills */}
      <div className="flex gap-3">
        {[
          { label: 'Available', count: available, color: 'text-emerald-700 bg-emerald-50' },
          { label: 'Reserved',  count: reserved,  color: 'text-amber-700 bg-amber-50' },
          { label: 'Faulty',    count: faulty,     color: 'text-red-600 bg-red-50' },
          { label: 'Total',     count: units.length, color: 'text-brand-600 bg-brand-50' },
        ].map(({ label, count, color }) => (
          <div key={label} className={`px-4 py-2 rounded-xl text-sm font-semibold ${color}`}>{count} {label}</div>
        ))}
      </div>

      {/* Filters + Add */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-44">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          <input className="input-field pl-9" placeholder="Search device, part code, serial…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="input-field w-auto" value={deviceFilter} onChange={e => setDeviceFilter(e.target.value)}>
          <option value="">All Devices</option>
          {DEVICE_TYPES.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select className="input-field w-auto" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          <option value="available">Available</option>
          <option value="reserved">Reserved</option>
          <option value="faulty">Faulty</option>
        </select>
        <button onClick={handleExportSpares} disabled={exporting}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50">
          {exporting
            ? <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
            : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
          }
          Download Excel
        </button>
        {!isReadOnly && (
          <button onClick={() => setShowAdd(true)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-colors ${colors.btn}`}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
            Add Spare
          </button>
        )}
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{background: 'linear-gradient(to right, #F5F3FC, #EAE7F8)'}}>
                <th className="px-4 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">Device</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">Part Code</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">Model</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">Serial Number</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">Ticket</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-brand-400 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-50">
              {loading ? (
                <tr><td colSpan={7} className="text-center py-12 text-gray-400 text-sm">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-16">
                  <p className="text-brand-500 font-semibold">No spare units found</p>
                  <p className="text-gray-400 text-sm mt-1">Add your first spare for {wh.label}</p>
                </td></tr>
              ) : filtered.map(u => {
                const sc = STATUS_CONFIG[u.status] || STATUS_CONFIG.available;
                return (
                  <tr key={u.id} className="hover:bg-brand-50/40 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-800 text-sm">{u.device_name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-brand-600">{u.part_code || '—'}</td>
                    <td className="px-4 py-3 text-gray-500 text-sm">{u.model || '—'}</td>
                    <td className="px-4 py-3 w-44">
                      {u.is_serialised === false ? (
                        <span className="text-xs text-gray-500">Qty: <span className="font-semibold text-gray-700">{u.quantity || 1}</span></span>
                      ) : (
                        <EditableCell value={u.serial_number} unitId={u.id} field="serial_number" placeholder="click to add" disabled={isReadOnly} />
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {u.status === 'reserved' && u.ticket_number && u.used_in_ticket ? (
                        <Link to={`/tickets/${u.used_in_ticket}`} className="text-brand-600 hover:text-brand-500 hover:underline font-semibold">
                          {u.ticket_number}
                        </Link>
                      ) : (
                        <span className="text-brand-600">{u.status === 'reserved' && u.ticket_number ? u.ticket_number : '—'}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${sc.badge}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                        {sc.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {isReadOnly ? null : deleteId === u.id ? (
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => handleDelete(u.id)} className="px-2 py-1 bg-red-500 text-white rounded text-xs font-semibold hover:bg-red-400">Delete</button>
                          <button onClick={() => setDeleteId(null)} className="px-2 py-1 border border-gray-200 rounded text-xs text-gray-500 hover:bg-gray-50">Cancel</button>
                        </div>
                      ) : (
                        <button onClick={() => setDeleteId(u.id)} className="text-gray-300 hover:text-red-400 p-1 rounded hover:bg-red-50 transition-colors">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {!loading && filtered.length > 0 && (
              <tfoot>
                <tr style={{background: 'linear-gradient(to right, #F5F3FC, #EAE7F8)'}}>
                  <td colSpan={6} className="px-4 py-2.5 text-xs font-bold text-brand-400 uppercase">{filtered.length} unit{filtered.length !== 1 ? 's' : ''}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {showAdd && <AddUnitModal warehouse={activeWh} onClose={() => setShowAdd(false)} onAdded={load} />}
    </div>
  );
}
